<template>
  <label
    :class="[$style['checkbox'], hideInput && $style['checkbox--no-input']]"
    @click="hideInput && emit('change', !checked)"
  >
    <input
      v-if="!hideInput"
      type="checkbox"
      :checked="checked"
      @change="handleChange"
      :class="$style['input']"
    />

    <span :class="$style['label']">
      <slot />
      {{ label }}
    </span>
  </label>
</template>
<script setup lang="ts">
const emit = defineEmits<{
  (e: 'change', value: boolean): void
}>()

defineProps<{
  checked?: boolean
  label?: string
  hideInput?: boolean
}>()

const handleChange = (event: Event) => {
  const target = event.target as HTMLInputElement
  emit('change', target.checked)
}
</script>
<style module>
.checkbox {
  display: grid;
  grid-template-columns: var(--spacing-xl) 1fr;
  align-items: center;
  gap: var(--spacing-m);
  cursor: pointer;
}

.checkbox--no-input {
  grid-template-columns: 1fr;
}

.label {
  align-items: center;
  display: flex;
  gap: var(--spacing-m);
}

.input {
  background-color: transparent;
  border: 1px solid var(--border-subtle);
  border-radius: var(--border-radius);
  cursor: pointer;
  width: var(--spacing-xl);
  height: var(--spacing-xl);
  appearance: none;
  position: relative;
  transition: all var(--duration-micro-fast) var(--easing-transition);
}

.input::before {
  background-color: var(--text-base);
  border-radius: calc(var(--border-radius) - 4px);
  content: "";
  display: flex;
  line-height: 1;
  width: calc(var(--spacing-xl) - 6px);
  height: calc(var(--spacing-xl) - 6px);
  position: absolute;
  top: 2px;
  left: 2px;
  opacity: 0;
  transform: scale(0.9);
  transition: all var(--duration-micro-normal) var(--easing-transition);
}

input:checked {
  border-color: var(--text-base);
}

.input:checked::before {
  opacity: 1;
  transform: scale(1);
}

.checkbox:hover input:not(:checked)::before {
  opacity: 0.25;
}

/* The public pages: amber when checked, and a visible keyboard focus */
:global(:where([data-theme="stage"])) .input:checked {
  border-color: var(--primary-color);
}

:global(:where([data-theme="stage"])) .input:checked::before {
  background-color: var(--primary-color);
}

.input:focus-visible {
  outline: 0.125rem solid var(--primary-color);
  outline-offset: 0.125rem;
}
</style>
